import { CaretDown, CaretUp, Star } from "@phosphor-icons/react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { InstrumentTypeResponse } from "../../codegen-api";
import {
  COLORS,
  ICON_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import {
  dailyChangeFraction,
  formatKnownPrice,
  formatRoiPercent,
  knownPrice,
} from "../../utils/unknownNumbers";
import { IMarket, MarketContext } from "../../contexts/MarketContext";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { useGetMarketSummary } from "../../hooks/api/marketSummary/useGetMarketSummary";
import usePointerSort from "../../hooks/usePointerSort";
import useWatchlist from "../../hooks/useWatchlist";
import { AssetResponse } from "../../utils/asset";
import HorizontalScroller from "../shared/HorizontalScroller";
import TickerLogo from "../shared/TickerLogo";
import {
  CollapseToggle,
  CollapsedBar,
  StripWrapper,
  Tile,
  TileChange,
  TilePrice,
  TileSymbol,
} from "./style";

function loadCollapsed(): boolean {
  try {
    return (
      localStorage.getItem(LocalStorageKeyEnum.WATCHLIST_STRIP_COLLAPSED) ===
      "1"
    );
  } catch {
    return false;
  }
}

function WatchlistTickerStrip() {
  const { market, setMarket } = useContext(MarketContext);
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const { watchlist, updateWatchlist } = useWatchlist();
  const { data: marketsSummaryData } = useGetMarketSummary();

  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed());

  useEffect(() => {
    try {
      localStorage.setItem(
        LocalStorageKeyEnum.WATCHLIST_STRIP_COLLAPSED,
        collapsed ? "1" : "0"
      );
    } catch {
      // ignore
    }
  }, [collapsed]);

  const tiles = useMemo(() => {
    if (!marketsSummaryData?.summaries || watchlist.length === 0) return [];
    const summaryByAsset: Record<string, any> = {};
    marketsSummaryData.summaries.forEach((s) => {
      summaryByAsset[s.asset as string] = s;
    });
    return watchlist
      .filter((w) => w.derivative === InstrumentTypeResponse.Perpetual)
      .map((w) => {
        const s = summaryByAsset[w.asset];
        if (!s) return null;
        // `/markets-summary` sends null for an unknown index/mark. Unknown stays
        // unknown: no $0.00 tile, and the change needs a real 24h reference
        // (index minus the MARK change mixed two prices and "succeeded" on null).
        return {
          asset: w.asset as AssetResponse,
          price: knownPrice(s.index_price),
          changePercent: dailyChangeFraction(
            s.perpetual_info?.mark_daily_change,
            s.perpetual_info?.mark_price_24h_ago
          ),
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [marketsSummaryData?.summaries, watchlist]);

  const onTileClick = useCallback(
    (asset: AssetResponse) => {
      setMarket({
        asset,
        derivative: InstrumentTypeResponse.Perpetual,
      });
    },
    [setMarket]
  );

  const onReorderTiles = useCallback(
    (fromIdx: number, toIdx: number) => {
      const perpEntries: IMarket[] = [];
      const others: IMarket[] = [];
      watchlist.forEach((w) => {
        if (w.derivative === InstrumentTypeResponse.Perpetual) {
          perpEntries.push(w);
        } else {
          others.push(w);
        }
      });
      const [moved] = perpEntries.splice(fromIdx, 1);
      perpEntries.splice(toIdx, 0, moved);
      updateWatchlist([...perpEntries, ...others]);
    },
    [watchlist, updateWatchlist]
  );

  // Pointer-events drag: works for mouse, touch, and pen.
  const sort = usePointerSort({ onReorder: onReorderTiles });

  if (tiles.length === 0) {
    return null;
  }

  if (collapsed) {
    return (
      <CollapsedBar>
        <span>
          <Star size={12} weight="fill" color={COLORS.highlight.one} />
          {tiles.length} in watchlist
        </span>
        <CollapseToggle
          type="button"
          aria-label="Show watchlist"
          onClick={() => setCollapsed(false)}
        >
          <CaretDown size={14} color={ICON_COLORS.two} />
        </CollapseToggle>
      </CollapsedBar>
    );
  }

  return (
    <StripWrapper>
      <HorizontalScroller>
        {tiles.map((tile, index) => {
          const changeKnown = tile.changePercent !== undefined;
          const positive = changeKnown && tile.changePercent! >= 0;
          const pricePrecision = getMarketPrecision(
            tile.asset,
            InstrumentTypeResponse.Perpetual
          ).price_precision;
          const active =
            market.asset === tile.asset &&
            market.derivative === InstrumentTypeResponse.Perpetual;
          return (
            <Tile
              key={tile.asset}
              type="button"
              active={active}
              dragging={sort.dragIndex === index}
              dragOver={sort.overIndex === index && sort.dragIndex !== index}
              onClick={() => onTileClick(tile.asset)}
              {...sort.getItemProps(index)}
            >
              <TickerLogo asset={tile.asset} size={20} />
              <TileSymbol>{tile.asset}-USD</TileSymbol>
              <TilePrice>
                {formatKnownPrice(tile.price, Math.max(pricePrecision || 2, 2))}
              </TilePrice>
              {changeKnown ? (
                <TileChange positive={positive}>
                  {formatRoiPercent(tile.changePercent)}
                </TileChange>
              ) : (
                // Neutral, not green: an unknown change is not "up".
                <TileChange positive style={{ color: TEXT_COLORS.three }}>
                  -
                </TileChange>
              )}
            </Tile>
          );
        })}
      </HorizontalScroller>
      <CollapseToggle
        type="button"
        aria-label="Hide watchlist"
        onClick={() => setCollapsed(true)}
        title="Hide watchlist strip"
      >
        <CaretUp size={14} color={ICON_COLORS.two} />
      </CollapseToggle>
    </StripWrapper>
  );
}

export default WatchlistTickerStrip;
